/* global HTMLDivElement */

import { makeStyles, tokens } from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import * as React from "react";
import { messageHistoryAtom } from "@/state";
import type { ChatMessage } from "@/types";
import { assistantMessageToCardData, qaBankMessageToCardData } from "@/utils/card-utils";
import Card from "../../cards";
import UserMessageCard from "./messages/user-message-card";

export default function ChatHistory() {
  const styles = useStyles();

  const ref = React.useRef<HTMLDivElement>(null);

  const messageHistory = useAtomValue(messageHistoryAtom);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [messageHistory]);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.content}>
        {messageHistory.map((message, index) => {
          return <MessageCard key={index} message={message} />;
        })}
      </div>
    </div>
  );
}

const MessageCard: React.FC<{ message: ChatMessage }> = ({ message }) => {
  if (message.role === "assistant") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Card data={assistantMessageToCardData(message)} variant="chat" />
      </div>
    );
  } else if (message.role === "user") {
    return <UserMessageCard message={message} />;
  } else if (message.role === "qa-bank") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Card data={qaBankMessageToCardData(message)} variant="chat" />
      </div>
    );
  } else {
    return null;
  }
};

const useStyles = makeStyles({
  container: {
    flex: "1 1 auto",
    minHeight: "100px",
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
    },
  },

  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "8px",
  },

  assistant_message__container: {
    display: "flex",
    justifyContent: "flex-start",
  },

  assistant_message: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "8px",
    marginRight: "48px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "16px",
    paddingBottom: "16px",
    minWidth: "60%",
  },

  assistant_message__answer: {
    fontSize: "12px",
    whiteSpace: "pre-wrap",
  },

  generating_message: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "12px",
    backgroundColor: "white",
    borderRadius: "8px",
    marginRight: "48px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "16px",
    paddingBottom: "16px",
  },

  user_message__container: {
    display: "flex",
    justifyContent: "flex-end",
  },

  user_message: {
    display: "flex",
    backgroundColor: tokens.colorPaletteLavenderBackground2,
    borderRadius: "8px",
    marginLeft: "48px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "16px",
    paddingBottom: "16px",
    fontSize: "12px",
  },

  table: {
    borderCollapse: "collapse",
    width: "100%",
    borderSpacing: "0",
  },
  th: {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#f4f4f4",
    fontWeight: "bold",
  },
  td: {
    border: "1px solid #ddd",
    padding: "8px",
  },
});
